import moment from 'moment-timezone'

import {
  RESIDENCY_LINK_ICON_NAMES,
  ResidencyLinkIconName,
} from '@/modules/residency/constants/ResidencyExternalLinkIcons'
import { upcomingPerformances } from '@/modules/residency/utils/performances'
import { canonicalHost } from '@/ssr/canonical'
import { buildMetaDescription } from '@/utils/metaDescription'
import { TitleVenue } from '@/utils/pageTitle'

import {
  PerformanceSimpleListRead,
  ResidencyExternalLinkRead,
  ResidencyRead,
  ShowRead,
} from '@generated/types'

export const SOCIAL_ICON_NAMES: ReadonlyArray<ResidencyLinkIconName> =
  RESIDENCY_LINK_ICON_NAMES.filter((name): name is ResidencyLinkIconName => name !== 'link')

export function socialUrlsFromLinks(links: ResidencyExternalLinkRead[] | undefined): string[] {
  if (!links?.length) return []
  return links
    .filter(link => (SOCIAL_ICON_NAMES as readonly string[]).includes(link.icon))
    .filter(link => /^https?:\/\//i.test(link.url))
    .map(link => link.url)
}

export function buildResidencyPath(slug: string, routePath: string): string {
  // Keep the route family the page is mounted on (/rd/ is the direct always-v2 route) so the
  // canonical doesn't leak traffic across routes, and build from the slug rather than route.path
  // so aliases, casing and trailing-slash variants don't echo back.
  return `${routePath.startsWith('/rd/') ? '/rd' : '/r'}/${slug}`
}

export function buildResidencyUrl(
  host: string | null | undefined,
  slug: string | null | undefined,
  routePath: string,
): string | null {
  // Same host treatment as rel=canonical, so the JSON-LD can't name a second spelling of the
  // page the tag already nominates.
  const hostname = canonicalHost(host)
  return hostname && slug ? `https://${hostname}${buildResidencyPath(slug, routePath)}` : null
}

export interface ResidencyJsonLdVenue extends TitleVenue {
  street_address: string
  postal_code: string
  country: string
  address_complete: boolean
  timezone: string
  venue_currency: string
}

const SCHEMA = 'https://schema.org'

// Escape chars that would break out of <script type="application/ld+json"> via v-html.
function scriptSafeJson(payload: unknown): string {
  return JSON.stringify(payload)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
}

function localIso(datetime: string, timezone: string): string {
  // Offset rather than the payload's UTC instant, so startDate reads as the time the page prints.
  return moment.tz(datetime, timezone).format()
}

function placeNode(venue: ResidencyJsonLdVenue): Record<string, unknown> {
  const node: Record<string, unknown> = { '@type': 'Place', name: venue.venue_name.trim() }
  // Google needs a street address and a country to be eligible, and the columns are all-or-nothing
  // behind a DB constraint — a half address is worse than none, so it's the flag that decides.
  if (venue.address_complete) {
    node.address = {
      '@type': 'PostalAddress',
      streetAddress: venue.street_address.trim(),
      addressLocality: venue.city.trim(),
      addressRegion: venue.state.trim(),
      postalCode: venue.postal_code.trim(),
      addressCountry: venue.country.trim(),
    }
  }
  return node
}

function offerNode(
  performance: PerformanceSimpleListRead,
  venue: ResidencyJsonLdVenue,
  url: string | null,
): Record<string, unknown> | null {
  // No price at all: all_in_price_range() returns null only where the performance has no undeleted
  // table, and Google rejects an Offer without a price ("Missing field 'price'"), which would take
  // the whole Event out of rich results. An Event without offers is still valid, so publish none.
  if (!performance.price_range) return null
  const offer: Record<string, unknown> = {
    '@type': 'Offer',
    priceCurrency: venue.venue_currency,
    // Already the cheapest all-in price a Guest can pay, which is what Google asks for. A sold-out
    // night still carries one: the backend falls back to the list range once nothing bookable is
    // priced, so the price says what the run charges and `sold` below says whether it is for sale.
    // The page prints the whole range, and AggregateOffer's lowPrice/highPrice would say that — but
    // Google's Event reference documents `offers` as an Offer only (AggregateOffer is a Product
    // shape), so the range would be bought with an undocumented type for nothing Google displays.
    price: performance.price_range.min,
    // The same signal the page prints as "(SOLD OUT)" — structured data that contradicts the page
    // is what Google's policy penalises.
    availability: performance.sold ? `${SCHEMA}/SoldOut` : `${SCHEMA}/InStock`,
  }
  if (url) offer.url = url
  if (performance.on_sale_datetime) {
    offer.validFrom = localIso(performance.on_sale_datetime, venue.timezone)
  }
  // The purchase stop the page already enforces, which is what validThrough means. With no explicit
  // stop the button dies at showtime, so omitting the expiry would keep the offer InStock past it —
  // the page greys the night out for the rest of the venue day while the markup still sells it.
  offer.validThrough = localIso(
    performance.off_sale_datetime ?? performance.datetime,
    venue.timezone,
  )
  return offer
}

// The `image` guard keeps the gray.png `get_srcset()` falls back to out of og:image and the Event
// node alike.
export function shareImage(show: ShowRead | undefined): string {
  return (show?.image && show.srcset?.rect2Xl?.src) || ''
}

export function shareImageDimensionsPx(
  show: ShowRead | undefined,
): { width: number; height: number } | null {
  return shareImage(show) ? { width: 1200, height: 800 } : null
}

interface EventContext {
  residency: ResidencyRead
  venue: ResidencyJsonLdVenue
  name: string
  // The page itself: absolute, or null when the host is unknown.
  url: string | null
  performerId: string
}

function eventNode(
  performance: PerformanceSimpleListRead,
  { residency, venue, name, url, performerId }: EventContext,
): Record<string, unknown> {
  // og:image and the meta description are built off the residency's own show, so the Event has to
  // resolve the same way or the picture the cards share stops being the picture the graph names.
  // Per field rather than one show for both: the performance's show fills a gap, it doesn't win.
  const description = buildMetaDescription(
    residency.description || residency.show?.description || performance.show?.description || '',
  )
  const image = shareImage(residency.show) || shareImage(performance.show)
  const node: Record<string, unknown> = {
    '@type': 'Event',
    '@id': `${url ?? ''}#event-${performance.id}`,
    name: performance.show?.name?.trim() || name,
    startDate: localIso(performance.datetime, venue.timezone),
    eventStatus: `${SCHEMA}/EventScheduled`,
    eventAttendanceMode: `${SCHEMA}/OfflineEventAttendanceMode`,
    location: placeNode(venue),
    performer: { '@type': 'PerformingGroup', '@id': performerId, name },
  }
  const offers = offerNode(performance, venue, url)
  if (offers) node.offers = offers
  if (url) node.url = url
  if (description) node.description = description
  if (image) node.image = [image]
  const venueName = venue.venue_name.trim()
  if (venueName) {
    node.organizer = { '@type': 'Organization', name: venueName }
  }
  // The whole run, not the cheapest tier: a free seat beside a $50 table is a paid event with a
  // free tier, and flagging it free is the page-contradicting kind of claim Google acts on.
  const { min, max } = performance.price_range ?? {}
  if (performance.price_range && Number(min) === 0 && Number(max) === 0) {
    node.isAccessibleForFree = true
  }
  return node
}

export function buildResidencyJsonLd(
  residency: ResidencyRead | null | undefined,
  venue: ResidencyJsonLdVenue,
  url: string | null,
): string | null {
  if (!residency) return null
  const sortedLinks = [...(residency.external_links ?? [])].sort((a, b) => a.position - b.position)
  const sameAs = socialUrlsFromLinks(sortedLinks)
  // Residency.name is blank=True; an unnamed run is no answer to "what's on", so the show the run
  // is built on supplies the title before we give up on the Event nodes altogether.
  const showName =
    residency.show?.name?.trim() ||
    (residency.performances ?? []).map(p => p.show?.name?.trim()).find(Boolean) ||
    ''
  const name = residency.name?.trim() || showName
  const performances = name ? upcomingPerformances(residency.performances, venue.timezone) : []
  if (!performances.length && !sameAs.length) return null

  const performerId = `${url ?? ''}#performer`
  const performer: Record<string, unknown> = { '@type': 'PerformingGroup', '@id': performerId }
  if (name) performer.name = name
  if (sameAs.length) performer.sameAs = sameAs

  const context: EventContext = { residency, venue, name, url, performerId }
  return scriptSafeJson({
    '@context': SCHEMA,
    '@graph': [performer, ...performances.map(performance => eventNode(performance, context))],
  })
}
